import { isIsoDateWithinModelCatalogWindow } from './model-catalog-recency.ts';

export const MODEL_STATUS_VALUES = ['active', 'preview', 'deprecated', 'unavailable'] as const;
export type ModelStatus = (typeof MODEL_STATUS_VALUES)[number];

export const TRI_STATE_VALUES = ['supported', 'unsupported', 'unknown'] as const;
export type TriState = (typeof TRI_STATE_VALUES)[number];

export const MODEL_MODALITIES = ['text', 'image', 'audio', 'video', 'file', 'embedding'] as const;
export type ModelModality = (typeof MODEL_MODALITIES)[number];

export const MODEL_USE_CASES = [
  'agents',
  'coding',
  'general',
  'image-generation',
  'local-deployment',
  'long-context',
  'multimodal',
  'value',
] as const;
export type ModelUseCase = (typeof MODEL_USE_CASES)[number];

export const AI_MODELS_ROOT_PATH = '/ai-model';

const MODEL_USE_CASE_SEARCH_TERMS: Record<ModelUseCase, string[]> = {
  agents: ['agent', '代理', '智能体', 'エージェント', '에이전트', 'agenten', 'agentes', 'agenci', 'агенты'],
  coding: ['code', 'programming', '编程', '代码', 'コーディング', 'プログラミング', '코딩', 'programmierung', 'programación', 'programmation', 'programowanie', 'programmeren', 'программирование'],
  general: ['general purpose', '通用', '汎用', '일반', 'allgemein', 'generalista', 'généraliste', 'ogólne', 'algemeen', 'универсальные'],
  'image-generation': ['image generation', '图像生成', '画像生成', '이미지 생성', 'bildgenerierung', 'generación de imágenes', 'génération d’images', 'generowanie obrazów', 'beeldgeneratie', 'генерация изображений'],
  'local-deployment': ['local deployment', '本地部署', 'ローカル展開', '로컬 배포', 'lokale bereitstellung', 'implementación local', 'déploiement local', 'wdrożenie lokalne', 'lokale implementatie', 'локальное развертывание'],
  'long-context': ['long context', '长上下文', '長いコンテキスト', '긴 컨텍스트', 'langer kontext', 'contexto largo', 'contexte long', 'długi kontekst', 'lange context', 'длинный контекст'],
  multimodal: ['multimodal', '多模态', 'マルチモーダル', '멀티모달', 'multimodal', 'мультимодальные'],
  value: ['value', 'cost effective', '性价比', '高コスパ', '가성비', 'preis-leistung', 'rentable', 'rapport qualité-prix', 'opłacalne', 'prijs-kwaliteit', 'выгодные'],
};

export type Confidence = 'verified' | 'inferred' | 'unverified' | 'conflicting';

export type SourceRecord = {
  label: string;
  publisher: string;
  sourceType: 'official' | 'aggregator' | 'model-card' | 'technical-report' | 'editorial';
  sourceUrl: string;
  fields: string[];
  retrievedAt: string;
  verifiedAt: string | null;
  confidence: Confidence;
};

export type ModelCapabilities = {
  reasoning: TriState;
  openWeights: TriState;
};

export type EndpointCapabilities = {
  toolCalling: TriState;
  structuredOutput: TriState;
  streaming: TriState;
  promptCache: TriState;
  batch: TriState;
  fineTuning: TriState;
};

export type ModelPrice = {
  inputPerMillion: number | null;
  outputPerMillion: number | null;
  cachedInputPerMillion: number | null;
  imageOutputPerMillion: number | null;
  currency: 'USD';
};

export type ModelEndpoint = {
  provider: string;
  providerSlug: string;
  endpointId: string;
  status: 'active' | 'preview' | 'unavailable';
  contextWindow: number | null;
  maxOutputTokens: number | null;
  inputModalities: ModelModality[];
  outputModalities: ModelModality[];
  capabilities: EndpointCapabilities;
  price: ModelPrice;
  sourceUrl: string;
  retrievedAt: string;
};

export type AiModel = {
  slug: string;
  name: string;
  shortName: string;
  officialModelId: string;
  organization: string;
  organizationSlug: string;
  family: string;
  familySlug: string;
  releaseDate: string | null;
  status: ModelStatus;
  summary: string;
  summaryZh: string;
  useCases: ModelUseCase[];
  strengths: string[];
  limitations: string[];
  nativeCapabilities: ModelCapabilities;
  license: string | null;
  endpoints: ModelEndpoint[];
  sourceRecords: SourceRecord[];
  lastVerifiedAt: string;
  accent: string;
};

export type ModelDirectoryItem = Pick<AiModel,
  'slug' | 'name' | 'shortName' | 'organization' | 'status' | 'summary' | 'accent'
> & {
  contextWindow: number | null;
  inputModalities: ModelModality[];
  inputPrice: number | null;
  outputPrice: number | null;
};

export function toModelDirectoryItem(model: AiModel, locale = 'en'): ModelDirectoryItem {
  const endpoint = model.endpoints[0];
  return {
    slug: model.slug,
    name: model.name,
    shortName: model.shortName,
    organization: model.organization,
    status: model.status,
    summary: locale.startsWith('zh') ? model.summaryZh : model.summary,
    accent: model.accent,
    contextWindow: endpoint?.contextWindow ?? null,
    inputModalities: endpoint?.inputModalities ?? [],
    inputPrice: endpoint?.price.inputPerMillion ?? null,
    outputPrice: endpoint?.price.outputPerMillion ?? null,
  };
}

const SNAPSHOT_DATE = '2026-08-17';
const OPENROUTER_MODELS_URL = 'https://openrouter.ai/api/v1/models';

const OFFICIAL_SOURCES: Record<string, string> = {
  openai: 'https://platform.openai.com/docs/models',
  anthropic: 'https://docs.anthropic.com/en/docs/about-claude/models/overview',
  google: 'https://ai.google.dev/gemini-api/docs/models',
  xai: 'https://docs.x.ai/docs/models',
  qwen: 'https://qwenlm.github.io/',
  deepseek: 'https://api-docs.deepseek.com/quick_start/pricing',
  mistral: 'https://docs.mistral.ai/getting-started/models/models_overview/',
  meta: 'https://ai.meta.com/llama/',
};

type ModelSeed = Omit<AiModel, 'endpoints' | 'sourceRecords' | 'lastVerifiedAt'> & {
  endpoint: Omit<ModelEndpoint, 'provider' | 'providerSlug' | 'status' | 'sourceUrl' | 'retrievedAt'>;
  officialSource: string;
};

function makeModel(seed: ModelSeed): AiModel {
  const sourceUrl = `https://openrouter.ai/${seed.endpoint.endpointId}`;
  return {
    ...seed,
    endpoints: [{
      ...seed.endpoint,
      provider: 'OpenRouter',
      providerSlug: 'openrouter',
      status: 'active',
      sourceUrl,
      retrievedAt: SNAPSHOT_DATE,
    }],
    sourceRecords: [
      {
        label: 'Official model documentation',
        publisher: seed.organization,
        sourceType: 'official',
        sourceUrl: seed.officialSource,
        fields: ['identity', 'family', 'native capabilities'],
        retrievedAt: SNAPSHOT_DATE,
        verifiedAt: null,
        confidence: 'unverified',
      },
      {
        label: 'OpenRouter model snapshot',
        publisher: 'OpenRouter',
        sourceType: 'aggregator',
        sourceUrl,
        fields: ['endpoint ID', 'modalities', 'context window', 'maximum output', 'pricing', 'supported parameters'],
        retrievedAt: SNAPSHOT_DATE,
        verifiedAt: SNAPSHOT_DATE,
        confidence: 'inferred',
      },
    ],
    lastVerifiedAt: SNAPSHOT_DATE,
  };
}

const supportedEndpoint = (
  overrides: Partial<EndpointCapabilities> = {},
): EndpointCapabilities => ({
  toolCalling: 'supported',
  structuredOutput: 'supported',
  streaming: 'unknown',
  promptCache: 'unknown',
  batch: 'unknown',
  fineTuning: 'unknown',
  ...overrides,
});

const price = (
  inputPerMillion: number | null,
  outputPerMillion: number | null,
  cachedInputPerMillion: number | null = null,
  imageOutputPerMillion: number | null = null,
): ModelPrice => ({
  inputPerMillion,
  outputPerMillion,
  cachedInputPerMillion,
  imageOutputPerMillion,
  currency: 'USD',
});

export const AI_MODELS: AiModel[] = [
  makeModel({
    slug: 'gpt-5-6-sol',
    name: 'GPT-5.6 Sol',
    shortName: 'GPT-5.6 Sol',
    officialModelId: 'openai/gpt-5.6-sol',
    organization: 'OpenAI',
    organizationSlug: 'openai',
    family: 'GPT-5.6',
    familySlug: 'gpt-5-6',
    releaseDate: '2026-07-09',
    status: 'active',
    summary: 'A flagship GPT-5.6 model listed for complex reasoning, coding and multi-step agent workflows.',
    summaryZh: 'GPT-5.6 系列旗舰模型，面向复杂推理、编程与多步骤 Agent 工作流。',
    useCases: ['agents', 'coding', 'long-context', 'multimodal'],
    strengths: ['1.05M-token provider context window', 'Text, image and file input', 'Tool and structured output parameters'],
    limitations: ['High provider token price in this snapshot', 'No comparable benchmark score in the current dataset'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#111827',
    officialSource: OFFICIAL_SOURCES.openai,
    endpoint: {
      endpointId: 'openai/gpt-5.6-sol',
      contextWindow: 1_050_000,
      maxOutputTokens: 128_000,
      inputModalities: ['text', 'image', 'file'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint({ promptCache: 'supported' }),
      price: price(5, 30, 0.5),
    },
  }),
  makeModel({
    slug: 'gpt-5-6-luna',
    name: 'GPT-5.6 Luna',
    shortName: 'GPT-5.6 Luna',
    officialModelId: 'openai/gpt-5.6-luna',
    organization: 'OpenAI',
    organizationSlug: 'openai',
    family: 'GPT-5.6',
    familySlug: 'gpt-5-6',
    releaseDate: '2026-07-09',
    status: 'active',
    summary: 'A lower-cost GPT-5.6 model for high-volume chat, classification and lightweight agent workflows.',
    summaryZh: '面向高并发对话、分类和轻量 Agent 工作流的低成本 GPT-5.6 模型。',
    useCases: ['agents', 'general', 'long-context', 'multimodal', 'value'],
    strengths: ['Low provider token price', '1.05M-token provider context window', 'Text, image and file input'],
    limitations: ['Positioning comes from a provider listing', 'No comparable benchmark score in the current dataset'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#374151',
    officialSource: OFFICIAL_SOURCES.openai,
    endpoint: {
      endpointId: 'openai/gpt-5.6-luna',
      contextWindow: 1_050_000,
      maxOutputTokens: 128_000,
      inputModalities: ['text', 'image', 'file'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint({ promptCache: 'supported' }),
      price: price(0.1, 0.6, 0.01),
    },
  }),
  makeModel({
    slug: 'claude-sonnet-5',
    name: 'Claude Sonnet 5',
    shortName: 'Sonnet 5',
    officialModelId: 'anthropic/claude-sonnet-5',
    organization: 'Anthropic',
    organizationSlug: 'anthropic',
    family: 'Claude',
    familySlug: 'claude',
    releaseDate: '2026-06-30',
    status: 'active',
    summary: 'A Sonnet-class model listed for coding, agents and professional knowledge work with adaptive reasoning.',
    summaryZh: '面向编程、Agent 与专业知识工作的 Sonnet 级模型，并支持自适应推理。',
    useCases: ['agents', 'coding', 'long-context', 'multimodal'],
    strengths: ['1M-token provider context window', 'Text, image and file input', 'Prompt cache pricing is exposed'],
    limitations: ['No comparable benchmark score in the current dataset', 'Provider behavior may differ from the native API'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#b45309',
    officialSource: OFFICIAL_SOURCES.anthropic,
    endpoint: {
      endpointId: 'anthropic/claude-sonnet-5',
      contextWindow: 1_000_000,
      maxOutputTokens: 128_000,
      inputModalities: ['text', 'image', 'file'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint({ promptCache: 'supported' }),
      price: price(2, 10, 0.2),
    },
  }),
  makeModel({
    slug: 'gemini-3-7-flash',
    name: 'Gemini 3.7 Flash',
    shortName: 'Gemini 3.7 Flash',
    officialModelId: 'google/gemini-3.7-flash',
    organization: 'Google',
    organizationSlug: 'google',
    family: 'Gemini',
    familySlug: 'gemini',
    releaseDate: '2026-08-13',
    status: 'active',
    summary: 'A fast multimodal Gemini model listed for responsive agent workflows, coding and multi-step reasoning.',
    summaryZh: '面向高响应 Agent 工作流、编程和多步骤推理的快速多模态 Gemini 模型。',
    useCases: ['agents', 'coding', 'long-context', 'multimodal', 'value'],
    strengths: ['Text, image, audio, video and file input', '1,048,576-token provider context window', 'Low provider token price'],
    limitations: ['Text output only on the selected endpoint', 'No comparable benchmark score in the current dataset'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#2563eb',
    officialSource: OFFICIAL_SOURCES.google,
    endpoint: {
      endpointId: 'google/gemini-3.7-flash',
      contextWindow: 1_048_576,
      maxOutputTokens: 65_536,
      inputModalities: ['text', 'image', 'audio', 'video', 'file'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint({ promptCache: 'supported' }),
      price: price(0.375, 1.875, 0.0375),
    },
  }),
  makeModel({
    slug: 'grok-4-6',
    name: 'Grok 4.6',
    shortName: 'Grok 4.6',
    officialModelId: 'x-ai/grok-4.6',
    organization: 'xAI',
    organizationSlug: 'xai',
    family: 'Grok',
    familySlug: 'grok',
    releaseDate: '2026-08-12',
    status: 'active',
    summary: 'A Grok model listed for coding, knowledge work and STEM tasks with text, image and file input.',
    summaryZh: '面向编程、知识工作与 STEM 任务的 Grok 模型，支持文本、图像和文件输入。',
    useCases: ['coding', 'general', 'long-context', 'multimodal'],
    strengths: ['500K-token provider context window', 'Text, image and file input', 'Tool and structured output parameters'],
    limitations: ['Long prompts can use a higher pricing tier', 'Maximum output is not specified in the current snapshot'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#18181b',
    officialSource: OFFICIAL_SOURCES.xai,
    endpoint: {
      endpointId: 'x-ai/grok-4.6',
      contextWindow: 500_000,
      maxOutputTokens: null,
      inputModalities: ['text', 'image', 'file'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint({ promptCache: 'supported' }),
      price: price(2, 6, 0.5),
    },
  }),
  makeModel({
    slug: 'qwen3-8-27b',
    name: 'Qwen3.8 27B',
    shortName: 'Qwen3.8 27B',
    officialModelId: 'qwen/qwen3.8-27b',
    organization: 'Alibaba / Qwen',
    organizationSlug: 'qwen',
    family: 'Qwen3.8',
    familySlug: 'qwen3-8',
    releaseDate: '2026-08-14',
    status: 'active',
    summary: 'An open-weight vision-language model listed for coding, research, multimodal interaction and agent tasks.',
    summaryZh: '面向编程、研究、多模态交互和 Agent 任务的开放权重视觉语言模型。',
    useCases: ['agents', 'coding', 'local-deployment', 'multimodal', 'value'],
    strengths: ['Open-weight model', 'Text, image and video input', 'Tool and structured output parameters'],
    limitations: ['License still needs field-level official verification', 'No comparable benchmark score in the current dataset'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'supported' },
    license: null,
    accent: '#7c3aed',
    officialSource: OFFICIAL_SOURCES.qwen,
    endpoint: {
      endpointId: 'qwen/qwen3.8-27b',
      contextWindow: 262_144,
      maxOutputTokens: 131_072,
      inputModalities: ['text', 'image', 'video'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint({ promptCache: 'supported' }),
      price: price(0.45, 3.2, 0.05),
    },
  }),
  makeModel({
    slug: 'deepseek-v4-pro-0813',
    name: 'DeepSeek V4 Pro 0813',
    shortName: 'DeepSeek V4 Pro',
    officialModelId: 'deepseek/deepseek-v4-pro-0813',
    organization: 'DeepSeek',
    organizationSlug: 'deepseek',
    family: 'DeepSeek V4',
    familySlug: 'deepseek-v4',
    releaseDate: '2026-08-12',
    status: 'active',
    summary: 'A text model listed as a large mixture-of-experts release with a 1,048,576-token provider context window.',
    summaryZh: '大规模混合专家文本模型，Provider 快照显示其上下文窗口为 1,048,576 Token。',
    useCases: ['agents', 'coding', 'long-context'],
    strengths: ['1,048,576-token provider context window', 'Large maximum output in the provider snapshot', 'Tool and structured output parameters'],
    limitations: ['Text input and output only', 'Provider price includes time-based overrides'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#315dff',
    officialSource: OFFICIAL_SOURCES.deepseek,
    endpoint: {
      endpointId: 'deepseek/deepseek-v4-pro-0813',
      contextWindow: 1_048_576,
      maxOutputTokens: 384_000,
      inputModalities: ['text'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint({ promptCache: 'supported' }),
      price: price(1.32, 3.96, 0.044),
    },
  }),
  makeModel({
    slug: 'mistral-medium-3-5',
    name: 'Mistral Medium 3.5',
    shortName: 'Mistral Medium 3.5',
    officialModelId: 'mistralai/mistral-medium-3-5',
    organization: 'Mistral AI',
    organizationSlug: 'mistral',
    family: 'Mistral Medium',
    familySlug: 'mistral-medium',
    releaseDate: '2026-04-30',
    status: 'active',
    summary: 'A dense instruction model listed for agent workflows, coding and complex professional tasks.',
    summaryZh: '面向 Agent 工作流、编程和复杂专业任务的稠密指令模型。',
    useCases: ['agents', 'coding', 'general', 'multimodal'],
    strengths: ['Text, image and file input', '262K-token provider context window', 'Tool and structured output parameters'],
    limitations: ['Maximum output is not specified in the current snapshot', 'Prompt cache pricing is not exposed'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#f97316',
    officialSource: OFFICIAL_SOURCES.mistral,
    endpoint: {
      endpointId: 'mistralai/mistral-medium-3-5',
      contextWindow: 262_144,
      maxOutputTokens: null,
      inputModalities: ['text', 'image', 'file'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint(),
      price: price(1.5, 7.5),
    },
  }),
  makeModel({
    slug: 'llama-4-maverick',
    name: 'Llama 4 Maverick',
    shortName: 'Llama 4 Maverick',
    officialModelId: 'meta-llama/llama-4-maverick',
    organization: 'Meta',
    organizationSlug: 'meta',
    family: 'Llama 4',
    familySlug: 'llama-4',
    releaseDate: '2025-04-05',
    status: 'active',
    summary: 'An open-weight multimodal mixture-of-experts model with text and image input.',
    summaryZh: '开放权重的多模态混合专家模型，支持文本和图像输入。',
    useCases: ['local-deployment', 'long-context', 'multimodal', 'value'],
    strengths: ['Open weights', 'Text and image input', '1,048,576-token provider context window'],
    limitations: ['Provider endpoint output is capped at 16,384 tokens', 'Reasoning support is unknown in the current evidence'],
    nativeCapabilities: { reasoning: 'unknown', openWeights: 'supported' },
    license: 'Llama 4 Community License',
    accent: '#0ea5e9',
    officialSource: OFFICIAL_SOURCES.meta,
    endpoint: {
      endpointId: 'meta-llama/llama-4-maverick',
      contextWindow: 1_048_576,
      maxOutputTokens: 16_384,
      inputModalities: ['text', 'image'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint(),
      price: price(0.2, 0.8),
    },
  }),
  makeModel({
    slug: 'qwen3-coder-next',
    name: 'Qwen3 Coder Next',
    shortName: 'Qwen3 Coder Next',
    officialModelId: 'qwen/qwen3-coder-next',
    organization: 'Alibaba / Qwen',
    organizationSlug: 'qwen',
    family: 'Qwen3 Coder',
    familySlug: 'qwen3-coder',
    releaseDate: '2026-02-03',
    status: 'active',
    summary: 'An open-weight coding model listed for coding agents and local development workflows.',
    summaryZh: '面向编程 Agent 与本地开发工作流的开放权重代码模型。',
    useCases: ['agents', 'coding', 'local-deployment', 'value'],
    strengths: ['Open-weight coding model', '262K-token provider context window', 'Low provider token price'],
    limitations: ['Text input and output only', 'No comparable coding benchmark in the current dataset'],
    nativeCapabilities: { reasoning: 'unknown', openWeights: 'supported' },
    license: null,
    accent: '#7c3aed',
    officialSource: OFFICIAL_SOURCES.qwen,
    endpoint: {
      endpointId: 'qwen/qwen3-coder-next',
      contextWindow: 262_144,
      maxOutputTokens: 262_144,
      inputModalities: ['text'],
      outputModalities: ['text'],
      capabilities: supportedEndpoint({ promptCache: 'supported' }),
      price: price(0.12, 0.8, 0.07),
    },
  }),
  makeModel({
    slug: 'gpt-5-4-image-2',
    name: 'GPT-5.4 Image 2',
    shortName: 'GPT-5.4 Image 2',
    officialModelId: 'openai/gpt-5.4-image-2',
    organization: 'OpenAI',
    organizationSlug: 'openai',
    family: 'GPT Image',
    familySlug: 'gpt-image',
    releaseDate: '2026-04-21',
    status: 'active',
    summary: 'A multimodal endpoint listed for workflows that combine reasoning, text and image generation.',
    summaryZh: '用于组合推理、文本与图像生成工作流的多模态 Endpoint。',
    useCases: ['image-generation', 'multimodal'],
    strengths: ['Text and image output', 'Text, image and file input', 'Structured output parameters'],
    limitations: ['Image output uses a separate pricing unit', 'Tool calling is unknown on the selected endpoint'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#111827',
    officialSource: OFFICIAL_SOURCES.openai,
    endpoint: {
      endpointId: 'openai/gpt-5.4-image-2',
      contextWindow: 272_000,
      maxOutputTokens: 128_000,
      inputModalities: ['text', 'image', 'file'],
      outputModalities: ['text', 'image'],
      capabilities: supportedEndpoint({ toolCalling: 'unknown', promptCache: 'supported' }),
      price: price(8, 15, 2, 30),
    },
  }),
  makeModel({
    slug: 'gemini-3-1-flash-image',
    name: 'Gemini 3.1 Flash Image',
    shortName: 'Gemini 3.1 Flash Image',
    officialModelId: 'google/gemini-3.1-flash-image',
    organization: 'Google',
    organizationSlug: 'google',
    family: 'Gemini Image',
    familySlug: 'gemini-image',
    releaseDate: '2026-06-18',
    status: 'active',
    summary: 'A Gemini endpoint listed for image generation and editing with text and image output.',
    summaryZh: '面向图像生成与编辑的 Gemini Endpoint，可输出文本和图像。',
    useCases: ['image-generation', 'multimodal'],
    strengths: ['Text and image output', 'Text and image input', 'Lower text token price than the other image sample'],
    limitations: ['Image output uses a separate pricing unit', 'Tool calling and prompt cache are unknown'],
    nativeCapabilities: { reasoning: 'supported', openWeights: 'unknown' },
    license: null,
    accent: '#2563eb',
    officialSource: OFFICIAL_SOURCES.google,
    endpoint: {
      endpointId: 'google/gemini-3.1-flash-image',
      contextWindow: 131_072,
      maxOutputTokens: 32_768,
      inputModalities: ['text', 'image'],
      outputModalities: ['text', 'image'],
      capabilities: supportedEndpoint({ toolCalling: 'unknown' }),
      price: price(0.5, 3, null, 60),
    },
  }),
];

const STATIC_MODEL_CATALOG_BOOTSTRAP_AT = new Date('2026-08-17T23:59:59.999Z');

export function getInitialCuratedAiModels(initializedAt: Date = STATIC_MODEL_CATALOG_BOOTSTRAP_AT): AiModel[] {
  return AI_MODELS.filter((model) => isIsoDateWithinModelCatalogWindow(model.releaseDate, initializedAt));
}

export const FEATURED_COMPARISONS = new Set([
  'claude-sonnet-5-vs-gemini-3-7-flash',
  'claude-sonnet-5-vs-gpt-5-6-sol',
  'gemini-3-7-flash-vs-gpt-5-6-luna',
]);

export type ModelSort = 'featured' | 'newest' | 'context' | 'price' | 'name';
export type ModelFilters = {
  query: string;
  useCase: ModelUseCase | '';
  input: ModelModality | '';
  output: ModelModality | '';
  openWeights: boolean;
  price: '' | 'under-1' | 'under-5' | 'under-20';
  context: '' | '128k' | '256k' | '1m';
  provider: string;
  sort: ModelSort;
};

type SearchValue = string | string[] | undefined;

function firstSearchValue(value: SearchValue): string {
  return (Array.isArray(value) ? value[0] : value) || '';
}

export function sanitizeModelQuery(value: SearchValue): string {
  return firstSearchValue(value)
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}\s./+-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

export function parseModelFilters(params: Record<string, SearchValue>): ModelFilters {
  const useCase = firstSearchValue(params.useCase);
  const input = firstSearchValue(params.input);
  const output = firstSearchValue(params.output);
  const priceFilter = firstSearchValue(params.price);
  const contextFilter = firstSearchValue(params.context);
  const sort = firstSearchValue(params.sort);

  return {
    query: sanitizeModelQuery(params.q),
    useCase: MODEL_USE_CASES.includes(useCase as ModelUseCase) ? useCase as ModelUseCase : '',
    input: MODEL_MODALITIES.includes(input as ModelModality) ? input as ModelModality : '',
    output: MODEL_MODALITIES.includes(output as ModelModality) ? output as ModelModality : '',
    openWeights: firstSearchValue(params.open) === 'yes',
    price: ['under-1', 'under-5', 'under-20'].includes(priceFilter)
      ? priceFilter as ModelFilters['price']
      : '',
    context: ['128k', '256k', '1m'].includes(contextFilter)
      ? contextFilter as ModelFilters['context']
      : '',
    provider: sanitizeModelQuery(params.provider),
    sort: ['newest', 'context', 'price', 'name'].includes(sort) ? sort as ModelSort : 'featured',
  };
}

function endpointFor(model: AiModel, provider?: string): ModelEndpoint | null {
  if (provider !== undefined) {
    return model.endpoints.find((endpoint) => endpoint.providerSlug === provider) || null;
  }
  return model.endpoints[0] || null;
}

function combinedTokenPrice(model: AiModel): number {
  const selected = endpointFor(model);
  if (!selected || selected.price.inputPerMillion === null || selected.price.outputPerMillion === null) {
    return Number.POSITIVE_INFINITY;
  }
  return selected.price.inputPerMillion + selected.price.outputPerMillion;
}

export function filterModels(models: AiModel[], filters: ModelFilters): AiModel[] {
  const curatedOrder = new Map(models.map((model, index) => [model.slug, index]));
  const query = filters.query.toLocaleLowerCase();
  const priceCaps: Record<Exclude<ModelFilters['price'], ''>, number> = {
    'under-1': 1,
    'under-5': 5,
    'under-20': 20,
  };
  const contextMinimums: Record<Exclude<ModelFilters['context'], ''>, number> = {
    '128k': 128_000,
    '256k': 256_000,
    '1m': 1_000_000,
  };

  const filtered = models.filter((model) => {
    const endpoint = endpointFor(model);
    if (!endpoint) return false;
    if (query) {
      const haystack = [
        model.name,
        model.officialModelId,
        model.organization,
        model.family,
        model.summary,
        model.summaryZh,
        ...model.useCases,
        ...model.useCases.flatMap((useCase) => MODEL_USE_CASE_SEARCH_TERMS[useCase]),
      ].join(' ').toLocaleLowerCase();
      if (!haystack.includes(query)) return false;
    }
    if (filters.useCase && !model.useCases.includes(filters.useCase)) return false;
    if (filters.input && !endpoint.inputModalities.includes(filters.input)) return false;
    if (filters.output && !endpoint.outputModalities.includes(filters.output)) return false;
    if (filters.openWeights && model.nativeCapabilities.openWeights !== 'supported') return false;
    if (filters.provider && model.organizationSlug !== filters.provider) return false;
    if (filters.context && (endpoint.contextWindow || 0) < contextMinimums[filters.context]) return false;
    if (filters.price && combinedTokenPrice(model) >= priceCaps[filters.price]) return false;
    return true;
  });

  return filtered.sort((a, b) => {
    if (filters.sort === 'name') return a.name.localeCompare(b.name);
    if (filters.sort === 'newest') return (b.releaseDate || '').localeCompare(a.releaseDate || '');
    if (filters.sort === 'context') return (endpointFor(b)?.contextWindow || 0) - (endpointFor(a)?.contextWindow || 0);
    if (filters.sort === 'price') return combinedTokenPrice(a) - combinedTokenPrice(b);
    return (curatedOrder.get(a.slug) || 0) - (curatedOrder.get(b.slug) || 0);
  });
}

export function getModelBySlug(slug: string, models: AiModel[] = AI_MODELS): AiModel | null {
  return models.find((model) => model.slug === slug) || null;
}

export function localizedModelPath(locale: string, path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return locale === 'en' ? normalized : `/${locale}${normalized}`;
}

export function buildModelsHref(locale: string, filters?: Partial<ModelFilters>): string {
  const params = new URLSearchParams();
  if (filters?.query) params.set('q', filters.query);
  if (filters?.useCase) params.set('useCase', filters.useCase);
  if (filters?.input) params.set('input', filters.input);
  if (filters?.output) params.set('output', filters.output);
  if (filters?.openWeights) params.set('open', 'yes');
  if (filters?.price) params.set('price', filters.price);
  if (filters?.context) params.set('context', filters.context);
  if (filters?.provider) params.set('provider', filters.provider);
  if (filters?.sort && filters.sort !== 'featured') params.set('sort', filters.sort);
  const query = params.toString();
  const path = localizedModelPath(locale, AI_MODELS_ROOT_PATH);
  return query ? `${path}?${query}` : path;
}

export function canonicalComparisonSlug(firstSlug: string, secondSlug: string): string {
  return [firstSlug, secondSlug].sort((a, b) => a.localeCompare(b)).join('-vs-');
}

export function buildComparisonPath(locale: string, firstSlug: string, secondSlug: string): string {
  return localizedModelPath(locale, `${AI_MODELS_ROOT_PATH}/compare/${canonicalComparisonSlug(firstSlug, secondSlug)}`);
}

export function parseComparisonSlug(pair: string, models: AiModel[] = AI_MODELS): [AiModel, AiModel] | null {
  for (const first of models) {
    const prefix = `${first.slug}-vs-`;
    if (!pair.startsWith(prefix)) continue;
    const second = getModelBySlug(pair.slice(prefix.length), models);
    if (second && first.slug !== second.slug) return [first, second];
  }
  return null;
}

export type ComparisonWinner = string | 'tie' | 'insufficient_data';
export type ComparisonRow = {
  key: string;
  type: 'higher_is_better' | 'lower_is_better' | 'boolean_supported' | 'set_contains' | 'informational';
  first: string | number | string[] | TriState | null;
  second: string | number | string[] | TriState | null;
  winner: ComparisonWinner;
  reasonCode: string;
  sourceScope: 'model' | 'endpoint';
};

function numericWinner(first: number | null, second: number | null, higher = true, firstSlug = '', secondSlug = ''): ComparisonWinner {
  if (first === null || second === null) return 'insufficient_data';
  if (first === second) return 'tie';
  return (higher ? first > second : first < second) ? firstSlug : secondSlug;
}

function triStateWinner(first: TriState, second: TriState, firstSlug: string, secondSlug: string): ComparisonWinner {
  if (first === 'unknown' || second === 'unknown') return 'insufficient_data';
  if (first === second) return 'tie';
  return first === 'supported' ? firstSlug : secondSlug;
}

export function compareModels(first: AiModel, second: AiModel, provider = 'openrouter'): ComparisonRow[] {
  const firstEndpoint = endpointFor(first, provider);
  const secondEndpoint = endpointFor(second, provider);
  const f = firstEndpoint;
  const s = secondEndpoint;
  const rows: ComparisonRow[] = [
    {
      key: 'contextWindow',
      type: 'higher_is_better',
      first: f?.contextWindow ?? null,
      second: s?.contextWindow ?? null,
      winner: numericWinner(f?.contextWindow ?? null, s?.contextWindow ?? null, true, first.slug, second.slug),
      reasonCode: 'context_window_larger',
      sourceScope: 'endpoint',
    },
    {
      key: 'maxOutputTokens',
      type: 'higher_is_better',
      first: f?.maxOutputTokens ?? null,
      second: s?.maxOutputTokens ?? null,
      winner: numericWinner(f?.maxOutputTokens ?? null, s?.maxOutputTokens ?? null, true, first.slug, second.slug),
      reasonCode: 'max_output_larger',
      sourceScope: 'endpoint',
    },
    {
      key: 'inputModalities',
      type: 'set_contains',
      first: f?.inputModalities || [],
      second: s?.inputModalities || [],
      winner: 'insufficient_data',
      reasonCode: 'input_modalities_differ',
      sourceScope: 'endpoint',
    },
    {
      key: 'outputModalities',
      type: 'set_contains',
      first: f?.outputModalities || [],
      second: s?.outputModalities || [],
      winner: 'insufficient_data',
      reasonCode: 'output_modalities_differ',
      sourceScope: 'endpoint',
    },
    {
      key: 'toolCalling',
      type: 'boolean_supported',
      first: f?.capabilities.toolCalling || 'unknown',
      second: s?.capabilities.toolCalling || 'unknown',
      winner: triStateWinner(f?.capabilities.toolCalling || 'unknown', s?.capabilities.toolCalling || 'unknown', first.slug, second.slug),
      reasonCode: 'tool_calling_support',
      sourceScope: 'endpoint',
    },
    {
      key: 'structuredOutput',
      type: 'boolean_supported',
      first: f?.capabilities.structuredOutput || 'unknown',
      second: s?.capabilities.structuredOutput || 'unknown',
      winner: triStateWinner(f?.capabilities.structuredOutput || 'unknown', s?.capabilities.structuredOutput || 'unknown', first.slug, second.slug),
      reasonCode: 'structured_output_support',
      sourceScope: 'endpoint',
    },
    {
      key: 'reasoning',
      type: 'informational',
      first: first.nativeCapabilities.reasoning,
      second: second.nativeCapabilities.reasoning,
      winner: 'insufficient_data',
      reasonCode: 'reasoning_support',
      sourceScope: 'model',
    },
    {
      key: 'openWeights',
      type: 'boolean_supported',
      first: first.nativeCapabilities.openWeights,
      second: second.nativeCapabilities.openWeights,
      winner: triStateWinner(first.nativeCapabilities.openWeights, second.nativeCapabilities.openWeights, first.slug, second.slug),
      reasonCode: 'open_weights_support',
      sourceScope: 'model',
    },
    {
      key: 'inputPrice',
      type: 'lower_is_better',
      first: f?.price.inputPerMillion ?? null,
      second: s?.price.inputPerMillion ?? null,
      winner: numericWinner(f?.price.inputPerMillion ?? null, s?.price.inputPerMillion ?? null, false, first.slug, second.slug),
      reasonCode: 'input_price_lower',
      sourceScope: 'endpoint',
    },
    {
      key: 'outputPrice',
      type: 'lower_is_better',
      first: f?.price.outputPerMillion ?? null,
      second: s?.price.outputPerMillion ?? null,
      winner: numericWinner(f?.price.outputPerMillion ?? null, s?.price.outputPerMillion ?? null, false, first.slug, second.slug),
      reasonCode: 'output_price_lower',
      sourceScope: 'endpoint',
    },
  ];
  return rows;
}

export function sameComparisonValue(row: ComparisonRow): boolean {
  if (Array.isArray(row.first) && Array.isArray(row.second)) {
    return [...row.first].sort().join('|') === [...row.second].sort().join('|');
  }
  return row.first === row.second;
}

export function calculateMonthlyCost({
  model,
  inputTokens,
  outputTokens,
  cachedInputTokens = 0,
  provider = 'openrouter',
}: {
  model: AiModel;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens?: number;
  provider?: string;
}): number | null {
  const endpoint = endpointFor(model, provider);
  if (!endpoint || endpoint.price.inputPerMillion === null || endpoint.price.outputPerMillion === null) return null;
  const cachedPrice = endpoint.price.cachedInputPerMillion ?? endpoint.price.inputPerMillion;
  const billableInput = Math.max(0, inputTokens);
  const billableCachedInput = Math.min(billableInput, Math.max(0, cachedInputTokens));
  const uncachedInput = billableInput - billableCachedInput;
  return (
    uncachedInput / 1_000_000 * endpoint.price.inputPerMillion
    + billableCachedInput / 1_000_000 * cachedPrice
    + Math.max(0, outputTokens) / 1_000_000 * endpoint.price.outputPerMillion
  );
}

const UNKNOWN_LABELS: Record<string, string> = {
  en: 'Unknown', zh: '未知', ja: '不明', ko: '알 수 없음', de: 'Unbekannt', es: 'Desconocido', fr: 'Inconnu', ru: 'Неизвестно', pl: 'Nieznane', nl: 'Onbekend',
};

function unknownLabel(locale: string): string {
  return UNKNOWN_LABELS[locale.toLocaleLowerCase().split('-')[0]] || UNKNOWN_LABELS.en;
}

export function formatTokenCount(value: number | null, locale = 'en'): string {
  if (value === null) return unknownLabel(locale);
  if (value >= 1_000_000) {
    const amount = value / 1_000_000;
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: amount >= 10 ? 0 : 2 }).format(amount)}M`;
  }
  if (value >= 1_000) {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value / 1_000)}K`;
  }
  return new Intl.NumberFormat(locale).format(value);
}

export function formatUsdPrice(value: number | null, locale = 'en'): string {
  if (value === null) return unknownLabel(locale);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: value < 1 ? 4 : 2,
  }).format(value);
}

export function formatModelDate(value: string | null, locale = 'en'): string {
  if (!value) return unknownLabel(locale);
  const normalizedValue = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value}T00:00:00Z`
    : value;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return unknownLabel(locale);
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
}

export type Recommendation = {
  category: 'coding' | 'agents' | 'long_context' | 'monthly_cost' | 'open_weights';
  winner: ComparisonWinner;
  reasonCode: string;
  evidence: string;
  confidence: 'high' | 'medium' | 'low';
};

export function buildRecommendations(
  first: AiModel,
  second: AiModel,
  inputTokens = 1_000_000,
  outputTokens = 200_000,
  cachedInputTokens = 0,
): Recommendation[] {
  const comparison = compareModels(first, second);
  const context = comparison.find((row) => row.key === 'contextWindow')!;
  const openWeights = comparison.find((row) => row.key === 'openWeights')!;
  const firstCost = calculateMonthlyCost({ model: first, inputTokens, outputTokens, cachedInputTokens });
  const secondCost = calculateMonthlyCost({ model: second, inputTokens, outputTokens, cachedInputTokens });
  const costWinner = numericWinner(firstCost, secondCost, false, first.slug, second.slug);

  return [
    {
      category: 'coding',
      winner: 'insufficient_data',
      reasonCode: 'comparable_coding_benchmark_missing',
      evidence: 'No same-source coding benchmark is attached to both model versions.',
      confidence: 'low',
    },
    {
      category: 'agents',
      winner: 'insufficient_data',
      reasonCode: 'comparable_agent_benchmark_missing',
      evidence: 'Capabilities are listed, but a comparable agent benchmark is missing.',
      confidence: 'low',
    },
    {
      category: 'long_context',
      winner: context.winner,
      reasonCode: context.reasonCode,
      evidence: `${first.shortName}=${context.first ?? 'unknown'}; ${second.shortName}=${context.second ?? 'unknown'}`,
      confidence: context.winner === 'insufficient_data' ? 'low' : 'medium',
    },
    {
      category: 'monthly_cost',
      winner: costWinner,
      reasonCode: 'same_provider_monthly_cost_lower',
      evidence: `${first.shortName}=${firstCost ?? 'unknown'}; ${second.shortName}=${secondCost ?? 'unknown'}; input=${inputTokens}; cached_input=${Math.min(Math.max(0, cachedInputTokens), Math.max(0, inputTokens))}; output=${outputTokens}; provider=OpenRouter`,
      confidence: costWinner === 'insufficient_data' ? 'low' : 'high',
    },
    {
      category: 'open_weights',
      winner: openWeights.winner,
      reasonCode: openWeights.reasonCode,
      evidence: `${first.shortName}=${openWeights.first}; ${second.shortName}=${openWeights.second}`,
      confidence: openWeights.winner === 'insufficient_data' ? 'low' : 'medium',
    },
  ];
}

export function serializeModelForApi(model: AiModel) {
  return {
    ...model,
    sourcePolicy: {
      aggregatorDiscoveryUrl: OPENROUTER_MODELS_URL,
      modelAndEndpointSeparated: true,
    },
  };
}
